import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  DownloadCloud,
  FolderInput,
  Loader2,
  Lock,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import toast from 'react-hot-toast';
import GlobalHeader from '../components/layout/GlobalHeader';
import { DEFAULT_GITHUB_LINKS } from '../constants/countryDashboard';
import { syncDataAPI } from '../services/api';

const formatWhen = (value) => {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleString();
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const COPY_WAIT_MS = 10 * 60 * 1000;
const COPY_POLL_MS = 1000;

function isCopyCompleteForStart(lastCopy, copyStartedAt) {
  if (!copyStartedAt || !lastCopy) return false;
  if (String(lastCopy.at || '') !== String(copyStartedAt)) return false;
  // In-progress marker from the server has finishedAt=null / running=true.
  if (lastCopy.running) return false;
  return Boolean(lastCopy.finishedAt) || lastCopy.error != null;
}

const emptyStatus = {
  configured: false,
  tokenRequired: false,
  authorized: true,
  sourceLabel: 'not configured',
  pendingCount: 0,
  pendingSample: [],
  pendingPublish: false,
  copyRunning: false,
  publishRunning: false,
  lastCopy: null,
  lastPublish: null,
  peekError: null,
  authRequired: false,
  shareUser: '',
  shareLabel: 'storage box',
  kanvaultUser: '',
  projectRoot: '',
  defaultTarget: '',
  targets: [],
};

function StatusPill({ tone, children }) {
  const tones = {
    ready: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
    wait: 'bg-amber-50 text-amber-800 ring-1 ring-amber-200',
    off: 'bg-white/70 text-vervestacks-primary ring-1 ring-vervestacks-primary/20',
    run: 'bg-vervestacks-primary text-white',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${tones[tone] || tones.off}`}>
      {children}
    </span>
  );
}

function parentFolderId(id) {
  const normalized = String(id || '').replace(/\//g, '\\');
  const splitAt = normalized.lastIndexOf('\\');
  return splitAt === -1 ? '' : normalized.slice(0, splitAt);
}

function buildFolderTree(targets) {
  const rootItem = (targets || []).find((item) => !item.id);
  const root = {
    id: '',
    name: 'Project root',
    path: rootItem?.path || '',
    children: [],
  };
  const byId = new Map([['', root]]);
  const folders = (targets || [])
    .filter((item) => item.id)
    .map((item) => ({ ...item, id: String(item.id).replace(/\//g, '\\') }))
    .sort((a, b) => a.id.split('\\').length - b.id.split('\\').length || a.id.localeCompare(b.id));
  folders.forEach((item) => {
    const parts = item.id.split('\\');
    const node = {
      id: item.id,
      name: parts[parts.length - 1],
      path: item.path,
      children: [],
    };
    const parent = byId.get(parentFolderId(item.id)) || root;
    parent.children.push(node);
    byId.set(item.id, node);
  });
  const sortNodes = (node) => {
    node.children.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    node.children.forEach(sortNodes);
  };
  sortNodes(root);
  return root;
}

function findFolder(node, id) {
  if (!node) return null;
  if (node.id === id) return node;
  for (const child of node.children) {
    const found = findFolder(child, id);
    if (found) return found;
  }
  return null;
}

function FolderBranch({ node, depth, expanded, selectedId, onChoose, onToggle }) {
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.id);
  const selected = node.id === selectedId;
  return (
    <div>
      <div className="flex items-center gap-0.5" style={{ paddingLeft: depth * 14 }}>
        {hasChildren ? (
          <button
            type="button"
            aria-label={isOpen ? `Hide folders inside ${node.name}` : `Show folders inside ${node.name}`}
            aria-expanded={isOpen}
            onClick={() => onToggle(node.id)}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-vervestacks-primary/70 hover:bg-[#F4F7FF]"
          >
            {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        ) : (
          <span className="h-7 w-7 shrink-0" />
        )}
        <button
          type="button"
          onClick={() => onChoose(node)}
          className={`min-w-0 flex-1 truncate rounded-lg px-2 py-1.5 text-left text-sm ${
            selected
              ? 'bg-[#D2DFFF] font-semibold text-vervestacks-primary'
              : 'text-slate-700 hover:bg-[#F4F7FF]'
          }`}
        >
          {node.name}
        </button>
      </div>
      {hasChildren && isOpen
        ? node.children.map((child) => (
          <FolderBranch
            key={child.id}
            node={child}
            depth={depth + 1}
            expanded={expanded}
            selectedId={selectedId}
            onChoose={onChoose}
            onToggle={onToggle}
          />
        ))
        : null}
    </div>
  );
}

function FolderPicker({ tree, selectedId, onSelect, disabled }) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set(['']));
  const boxRef = useRef(null);
  const selected = findFolder(tree, selectedId) || tree;

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!boxRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggle = (id) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const choose = (node) => {
    onSelect(node.id);
    if (node.children.length) {
      setExpanded((current) => {
        const next = new Set(current);
        next.add(node.id);
        return next;
      });
      return;
    }
    setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative mt-2">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center justify-between gap-3 rounded-2xl border border-vervestacks-primary/20 bg-[#F4F7FF] px-4 py-3 text-left text-sm font-medium text-vervestacks-primary outline-none focus:ring-2 focus:ring-vervestacks-accent disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span className="truncate">{selected.id ? selected.id : 'Project root'}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open ? (
        <div className="absolute z-30 mt-2 max-h-72 w-full overflow-auto rounded-2xl border border-vervestacks-primary/15 bg-white p-2 shadow-xl">
          <FolderBranch
            node={tree}
            depth={0}
            expanded={expanded}
            selectedId={selected.id}
            onChoose={choose}
            onToggle={toggle}
          />
        </div>
      ) : null}
    </div>
  );
}

function ActionButton({ enabled, running, onClick, children }) {
  const palette = 'bg-gradient-kanors text-white shadow-lg shadow-vervestacks-accent/30 hover:bg-gradient-kanors-reverse';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!enabled || running}
      className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-3.5 text-sm font-semibold tracking-wide transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-vervestacks-primary focus:ring-offset-2 ${
        enabled && !running
          ? `${palette} hover:scale-[1.02] active:scale-[0.98]`
          : 'cursor-not-allowed bg-slate-200 text-slate-500'
      }`}
    >
      {running ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
      {children}
    </button>
  );
}

const SyncDataPage = () => {
  const [status, setStatus] = useState(emptyStatus);
  const [loading, setLoading] = useState(true);
  const [tokenInput, setTokenInput] = useState(() => (
    typeof sessionStorage === 'undefined' ? '' : sessionStorage.getItem(syncDataAPI.tokenKey) || ''
  ));
  const [copyBusy, setCopyBusy] = useState(false);
  const [sharePassword, setSharePassword] = useState('');
  const [shareError, setShareError] = useState('');
  const [shareBusy, setShareBusy] = useState(false);
  const [target, setTarget] = useState('');
  const [targetReady, setTargetReady] = useState(false);
  const copyWaitGen = useRef(0);

  const [loadError, setLoadError] = useState(null);

  const applyStatus = useCallback((next) => {
    if (next?.authRequired) syncDataAPI.clearShareSession();
    const merged = { ...emptyStatus, ...next };
    setStatus(merged);
    setLoadError(null);
    setTarget((current) => {
      if (targetReady) return current;
      const preferred = next?.defaultTarget || '';
      const known = (next?.targets || []).some((item) => item.id === preferred);
      return known ? preferred : '';
    });
    setTargetReady(true);
    return merged;
  }, [targetReady]);

  const refresh = useCallback(async () => {
    try {
      const next = await syncDataAPI.getStatus();
      return applyStatus(next);
    } catch (err) {
      setLoadError(err.response?.data?.message || err.message || 'Could not load sync status.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [applyStatus]);

  const announceCopyResult = useCallback((lastCopy) => {
    if (!lastCopy) {
      toast.success('Copy finished.');
      return;
    }
    if (lastCopy.error) toast.error(lastCopy.error);
    else toast.success(`Copied ${lastCopy.copied || 0} file(s), removed ${lastCopy.deleted || 0}.`);
  }, []);

  const waitForCopyToFinish = useCallback(async (copyStartedAt, generation) => {
    const deadline = Date.now() + COPY_WAIT_MS;

    while (Date.now() < deadline) {
      if (copyWaitGen.current !== generation) return false;

      const next = await refresh();
      if (copyWaitGen.current !== generation) return false;
      if (!next) {
        await wait(COPY_POLL_MS);
        continue;
      }

      if (isCopyCompleteForStart(next.lastCopy, copyStartedAt)) {
        setCopyBusy(false);
        announceCopyResult(next.lastCopy);
        return true;
      }

      // Fallback for older backends that do not return copyStartedAt / running marker:
      // once the in-memory lock is gone and lastCopy is no longer "running", stop waiting.
      if (!copyStartedAt && !next.copyRunning && !next.lastCopy?.running) {
        setCopyBusy(false);
        announceCopyResult(next.lastCopy);
        return true;
      }

      await wait(COPY_POLL_MS);
    }

    if (copyWaitGen.current !== generation) return false;
    setCopyBusy(false);
    toast.error('Copy is taking too long. Refresh the page to check the latest status.');
    return false;
  }, [announceCopyResult, refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const running = status.copyRunning || copyBusy;
    const id = setInterval(() => refresh(), running ? 1500 : 5000);
    return () => clearInterval(id);
  }, [refresh, status.copyRunning, copyBusy]);

  const saveSharePassword = async (event) => {
    event.preventDefault();
    setShareBusy(true);
    setShareError('');
    try {
      const next = await syncDataAPI.connectShare(sharePassword);
      setSharePassword('');
      setStatus({ ...emptyStatus, ...next });
      setLoadError(null);
    } catch (err) {
      syncDataAPI.clearShareSession();
      setShareError(err.response?.data?.message || err.message || 'Could not open the sync share.');
    } finally {
      setShareBusy(false);
    }
  };

  const saveToken = (event) => {
    event.preventDefault();
    sessionStorage.setItem(syncDataAPI.tokenKey, tokenInput.trim());
    setLoading(true);
    refresh();
  };

  const targets = status.targets?.length
    ? status.targets
    : [{ id: '', label: 'Project root', path: status.projectRoot || '' }];
  const folderTree = buildFolderTree(targets);
  const selectedTarget = findFolder(folderTree, target) || folderTree;

  const startCopy = async () => {
    const generation = copyWaitGen.current + 1;
    copyWaitGen.current = generation;
    setCopyBusy(true);
    try {
      const started = await syncDataAPI.copy(selectedTarget?.id || '');
      await waitForCopyToFinish(started?.copyStartedAt || '', generation);
    } catch (err) {
      if (copyWaitGen.current === generation) {
        copyWaitGen.current += 1;
        setCopyBusy(false);
      }
      toast.error(err.response?.data?.message || err.message || 'Copy could not start.');
    }
  };

  const copyRunning = copyBusy || !!status.copyRunning;
  const canCopy = status.configured && status.authorized && !status.authRequired && !copyRunning && status.pendingCount > 0;

  return (
    <div className="min-h-screen bg-[#D2DFFF]">
      <GlobalHeader githubLinks={DEFAULT_GITHUB_LINKS} />

      <div className="relative overflow-hidden">
        <div className="pointer-events-none absolute -left-24 top-10 z-0 h-64 w-64 rounded-full bg-vervestacks-accent/25 blur-3xl" />
        <div className="pointer-events-none absolute -right-16 top-40 z-0 h-72 w-72 rounded-full bg-vervestacks-primary/20 blur-3xl" />

        <div className="relative z-10 mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 rounded-3xl border border-vervestacks-primary/10 bg-white p-6 shadow-xl sm:p-8"
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="mb-2 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-vervestacks-accent">
                  <Sparkles className="h-3.5 w-3.5" />
                  Operator tools
                </p>
                <h1 className="text-3xl font-bold tracking-tight text-vervestacks-primary sm:text-4xl">
                  Fetch files
                </h1>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 sm:text-base">
                  Copy changed files and folders onto this server. This does not change PostgreSQL
                  or what visitors currently see.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <StatusPill tone={status.configured ? 'ready' : 'off'}>
                  {status.configured ? status.sourceLabel : 'Not configured'}
                </StatusPill>
                {copyRunning ? (
                  <StatusPill tone="run">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Copying
                  </StatusPill>
                ) : (
                  <StatusPill tone="off">Idle</StatusPill>
                )}
              </div>
            </div>
          </motion.div>

          <AnimatePresence>
            {status.tokenRequired && !status.authorized ? (
              <motion.form
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                onSubmit={saveToken}
                className="mb-8 rounded-3xl border border-white/70 bg-white/80 p-6 shadow-xl backdrop-blur"
              >
                <div className="mb-4 flex items-center gap-3 text-vervestacks-primary">
                  <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-vervestacks-secondary">
                    <Lock className="h-5 w-5" />
                  </span>
                  <div>
                    <h2 className="font-semibold">Sync token required</h2>
                    <p className="text-sm text-vervestacks-primary/70">
                      Enter the server token to copy data. This page is not linked from the public site.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <input
                    type="password"
                    value={tokenInput}
                    onChange={(event) => setTokenInput(event.target.value)}
                    placeholder="SYNC_DATA_TOKEN"
                    className="w-full rounded-2xl border border-vervestacks-primary/20 bg-white px-4 py-3 text-sm text-vervestacks-primary outline-none ring-vervestacks-accent focus:ring-2"
                  />
                  <button type="submit" className="btn-primary whitespace-nowrap">
                    Unlock
                  </button>
                </div>
              </motion.form>
            ) : null}
          </AnimatePresence>

          <AnimatePresence>
            {status.authRequired && status.authorized ? (
              <motion.form
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                onSubmit={saveSharePassword}
                className="mb-8 rounded-3xl border border-white/70 bg-white/80 p-6 shadow-xl backdrop-blur"
              >
                <div className="mb-4 flex items-center gap-3 text-vervestacks-primary">
                  <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-vervestacks-secondary">
                    <Lock className="h-5 w-5" />
                  </span>
                  <div>
                    <h2 className="font-semibold">{(status.shareLabel || 'Share')} password</h2>
                    <p className="text-sm text-vervestacks-primary/70">
                      Sign in as {status.shareUser || status.kanvaultUser || 'user'} to read the changed files in this browser session.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <input
                    type="password"
                    value={sharePassword}
                    onChange={(event) => setSharePassword(event.target.value)}
                    placeholder={`${status.shareLabel || 'Share'} password`}
                    autoComplete="current-password"
                    className="w-full rounded-2xl border border-vervestacks-primary/20 bg-white px-4 py-3 text-sm text-vervestacks-primary outline-none ring-vervestacks-accent focus:ring-2"
                  />
                  <button type="submit" disabled={shareBusy} className="btn-primary whitespace-nowrap">
                    {shareBusy ? 'Checking…' : 'Show changed files'}
                  </button>
                </div>
                {shareError ? <p className="mt-3 text-sm text-red-700">{shareError}</p> : null}
              </motion.form>
            ) : null}
          </AnimatePresence>

          {!status.configured && !loading ? (
            <div className="mb-8 rounded-3xl border border-white/70 bg-white/80 p-6 text-vervestacks-primary shadow-xl backdrop-blur">
              Set <code className="rounded bg-vervestacks-secondary px-1.5 py-0.5 text-sm">ACTIVE_PROFILE</code> in
              {' '}<code className="rounded bg-vervestacks-secondary px-1.5 py-0.5 text-sm">config/syncDataProfile.js</code>
              {' '}to <code className="rounded bg-vervestacks-secondary px-1.5 py-0.5 text-sm">local</code>,
              {' '}<code className="rounded bg-vervestacks-secondary px-1.5 py-0.5 text-sm">server19</code>, or
              {' '}<code className="rounded bg-vervestacks-secondary px-1.5 py-0.5 text-sm">cloud</code>.
              Existing push-deploy workflows are unchanged.
            </div>
          ) : null}

          {loadError ? (
            <div className="mb-8 rounded-3xl border border-amber-200 bg-white p-4 text-sm text-amber-800 shadow">
              Could not reach the API yet. The page will keep checking. {loadError}
            </div>
          ) : null}
          {status.peekError && !status.authRequired ? (
            <div className="mb-8 rounded-3xl border border-red-200 bg-white p-4 text-sm text-red-700 shadow">
              Could not read pending files: {status.peekError}
            </div>
          ) : null}

          <div>
            <motion.section
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="rounded-3xl border border-white/80 bg-white p-6 shadow-xl sm:p-8"
            >
              <div className="mb-5 flex items-start justify-between gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-kanors text-white shadow-md">
                  <DownloadCloud className="h-6 w-6" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-vervestacks-primary">Fetch files and folders</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Copy waiting files and folders into the folder you choose on this server. This does not change PostgreSQL, Redis, or what visitors currently see.
              </p>
              <label className="mt-5 block">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-vervestacks-primary/60">
                  <FolderInput className="h-3.5 w-3.5" />
                  Copy into
                </span>
                <FolderPicker
                  tree={folderTree}
                  selectedId={selectedTarget.id}
                  onSelect={setTarget}
                  disabled={copyRunning || !status.authorized}
                />
                <span className="mt-2 block break-all font-mono text-[11px] leading-5 text-slate-500">
                  {selectedTarget?.path || 'Project root'}
                </span>
              </label>
              <div className="mt-5 rounded-2xl bg-[#F4F7FF] p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-vervestacks-primary/60">Waiting to copy</p>
                <p className="mt-1 text-3xl font-bold text-vervestacks-primary">{status.pendingCount}</p>
                {status.pendingSample?.length ? (
                  <ul className="mt-3 max-h-64 space-y-1 overflow-auto font-mono text-[11px] text-slate-500">
                    {status.pendingSample.map((row) => (
                      <li key={row} className="truncate">{row}</li>
                    ))}
                  </ul>
                ) : status.authRequired ? (
                  <p className="mt-2 text-sm text-slate-500">
                    Enter the {status.shareLabel || 'share'} password in this browser to read the changed files.
                  </p>
                ) : status.configured ? (
                  <p className="mt-2 text-sm text-slate-500">Nothing new on the source right now.</p>
                ) : (
                  <p className="mt-2 text-sm text-slate-500">Sync source is not turned on, so no files were read.</p>
                )}
              </div>
              {status.lastCopy ? (
                <p className="mt-4 text-xs text-slate-500">
                  Last copy {formatWhen(status.lastCopy.finishedAt || status.lastCopy.at)}
                  {status.lastCopy.error ? ` — ${status.lastCopy.error}` : ` — ${status.lastCopy.copied || 0} copied`}
                </p>
              ) : null}
              <ActionButton enabled={canCopy} running={copyRunning} onClick={startCopy}>
                {copyRunning ? 'Syncing files…' : 'Sync files'}
              </ActionButton>
            </motion.section>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mt-8 flex flex-col gap-4 rounded-3xl border border-white/70 bg-white/70 p-5 shadow-lg backdrop-blur sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex items-start gap-3 text-sm text-vervestacks-primary">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
              <p>
                This is an extra operator path. Git push deploys still work exactly as they do today.
              </p>
            </div>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-white px-4 py-2 text-sm font-semibold text-vervestacks-primary shadow-sm ring-1 ring-vervestacks-primary/15 hover:bg-vervestacks-secondary"
            >
              <CheckCircle2 className="h-4 w-4" />
              Back to site
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SyncDataPage;
